<template>
  <DocumentShareHeader v-if="isDocumentShared"/>
  <pdf-header @tailorResume="tailorResumeVisible=true" v-else/>
    
  <div class="w-full flex flex-row h-[calc(100vh-60px)]">
    <div id="onboarding-generator-points" data-step="2" class="h-full overflow-visible z-20">
        <transition
            enter-active-class="ease-in-out duration-500 transform"
            enter-from-class="-left-[620px] -translate-x-full"
            enter-to-class="-left-0 translate-x-0"

            leave-active-class="ease-in-out duration-500 transform"
            leave-from-class="translate-x-0 -left-0 "
            leave-to-class="-translate-x-full -left-[620px]"

            mode="out-in"
        >
            <div
                v-if="bulletOpened && !showResumeScorer"
                class="fixed h-full min-w-[618px] max-w-[618px] bg-white ml-[72px]"
            >
              <!-- bullet generator -->
              <pdf-bullet-generator class="border-r border-[#E9EEF2]" />
            </div>
        </transition>
			
        <div class="flex-none h-full">
            <sidebar @new-resource="handleNewResource" />
        </div>
    </div>

    <!-- main content -->
    <div class="flex flex-col flex-grow" :class="{ 'w-[calc(100%-618px)]': bulletOpened, 'w-[calc(100%-70px)]': !bulletOpened  }">
      <!-- top toolbar -->
      <div class="flex flex-col">
        <ActionsBar
          v-if="!isDocumentShared"
          :isOnCoverLetterEditor="coverLetter"
          :numberOfPages="nrOfPages"
          :currentPage="currentPage"
          :templates="templates"
          @change-page="builderToolsStore.scrollToPage"
          @regenerate="handleRegenerateCoverLetter"
          @regenerate-cover-letter-prompt="regenerateCoverLetterPromptHandler"
          @change-template="changeTemplateHandler"
        />
        
        <!-- ruler toolbar -->
        <div
            class="h-[15px] w-full overflow-auto won-tag-light-gray"
            v-if="!isDocumentShared"
        >
          <div class="h-full">
            <div
              id="pdf-ruler"
              data-step="6"
              v-if="loaded"
              :style="rulerStyles"
              class="flex mx-auto h-full justify-start items-center relative bg-white"
            >
              <pdf-ruler />
              <pdf-draggable-tick
                :side="'left'"
                @moved="v => builderStyle.changeMargin(v, 'left')"
                :style="{ 'left': rulerPaddings.leftPadding  + 'px' }"
                class="absolute cursor-ew-resize"
              />
              <pdf-draggable-tick
                :side="'right'"
                @moved="v => builderStyle.changeMargin(v, 'right')"
                :style="{ 'left': (builderStyle.getBuilderWidth - rulerPaddings.rightPadding) + 'px' }"
                class="absolute cursor-ew-resize"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- actual builder -->
      <div :class="[{'bg-[#656e83]': !coverLetter, 'bg-[#F1F5F7]': coverLetter}, 'overflow-auto h-full relative']" @scroll="hideContextMenu">
         <div class="mx-auto mt-[64px] mb-[40px]" v-if="loaded">
            <div
              class="bg-white builder-content m-auto relative"
              id="pages-nr-exceeded" data-step="3"
              :style="pageStyles"
              @click.right="rightClickHandler"
            >
                <div 
                  :class="['absolute border-dashed border-gray-300 left-0 right-0', `page-${page}`]"
                  :style="{'border-bottom-width':page > 1  ?'.1px' : '0px','margin-top': ((page-1)*pageHeight) + 'px'}"
                  v-for="(page, index) in nrOfPages"
                  :key="index"
                >
                  <span v-if="page>1" class="text-xs text-gray-300 pl-2 select-none absolute">
                    page {{ page }}
                  </span>
                </div>
            
                <div v-if="loading && coverLetter" class="absolute inset-0 flex items-center justify-center mt-[-300px] z-200">
                  <BigSpinner/>
                </div>
                
                <slot/>
            </div>
         </div>
        <ScorerCard
          v-if="isResumeScorerEnabled() && !coverLetter && !bulletOpened && !isDocumentShared"
        />
      </div>
    </div>

    <SidebarScorer
      v-if="isResumeScorerEnabled() && !coverLetter"
    />
  </div>

  <!-- create new cover letter -->
  <div v-if="coverLetter && false" class="fixed left-32 bottom-6">
    <div class="relative bg-white px-2 py-0.5 rounded-lg mb-2 -translate-x-[28%]">
      <span class="text-[#2A8176] font-[Inter] font-semibold text-xs">Create New Cover Letter</span>
    </div>
    <router-link :to="{ name: 'cover-letters.edit' }" @click="handleNewResource">
      <page-action class="bg-gradient-to-b from-[#073B5D] via-[#2A8176] to-[#2A8176]">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.6666 6.00033L13.5 4.16699L15.3333 3.33366L13.5 2.50033L12.6666 0.666992L11.8333 2.50033L9.99996 3.33366L11.8333 4.16699L12.6666 6.00033ZM7.66663 6.33366L5.99996 2.66699L4.33329 6.33366L0.666626 8.00033L4.33329 9.66699L5.99996 13.3337L7.66663 9.66699L11.3333 8.00033L7.66663 6.33366ZM12.6666 10.0003L11.8333 11.8337L9.99996 12.667L11.8333 13.5003L12.6666 15.3337L13.5 13.5003L15.3333 12.667L13.5 11.8337L12.6666 10.0003Z" fill="white"/>
        </svg>
      </page-action>
    </router-link>
  </div>

  <!-- triggering first time enter this page -->
  <BuilderOnboarding v-if="loaded && !coverLetter && token == null" />
  <CoverLetterOnboarding v-if="loaded && coverLetter && token == null" />

  <!-- trigger first time when clicking download -->
  <SurveyModal :visible="visibleSurvey"
    link="https://docs.google.com/forms/d/1cZyHdxqmgIa8qp5atYWcIjBHGHDlM5EJ6GbZyYgoK5g/"
    @survey-modal:close="handleCloseSurvey()" 
  />

  <change-template-modal
    v-if="loaded && !coverLetter"
    :templates="templates"
    :visible="changeTemplateVisible"
    @close="() => changeTemplateVisible=false"
    :currentTemplate="new String(data?.template).toLowerCase()"
    @change="handleChangeTemplate"
  />

  <TailorResume v-if="!coverLetter" :visible="tailorResumeVisible" @delete-modal:close="handleCloseTailorResume" />

  <!-- trigger for cover-letters -->
  <div id="onboarding-select-resume" data-step="1" class="absolute right-0">
    <div id="onboarding-select-resume-step2" data-step="2">
      <SelectResume
        v-if="isOnCoverLetterPage"
        @networking:limit-error="handleLimitError()"
        :visible="(!generatedPromptForCoverLetter && !isOnboarding) || openedCoverLetter"
        @delete-modal:close="handleCloseGeneratorPrompt"
      />
    </div>
  </div>

  <div v-if="isResumeScorerEnabled() && !coverLetter && !isDocumentShared">
    <ResumeScoreModal/>
  </div>

	<Teleport to="body">
	  <RemoveUserAccessModal/>
	</Teleport>
  <ResumeServiceNudge/>
  <LimitExceededModal />
  <ShareResumeModal
    v-if="!coverLetter"
    :resume="data"
    :resumeTitle="data?.title || 'notitle'"
  />
  <PDFContextMenu
    :show="showContextMenu"
    :hideContextMenu="hideContextMenu"
  />
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, shallowRef, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { getAll as getAllTemplates } from '../api/templates';
import { regenerateCoverLetter } from "../api/cover-letters";
import { isResumeScorerEnabled } from '../utils/helpers';
import shareResumeApi from "../api/share-resume";

import { useFeatureUsageStore } from '../store/feature-usage.store';
import { useBuilderStyles } from '../store/builder-style.store';
import { useBuilder } from '../store/builder.store';
import { useBulletGenerator } from '../store/bullet-store';
import { useOnboardingStore } from '../store/onboarding.store';
import { useResumeScorerStore } from '../store/scorer.store';
import { useTiptapStore } from "../store/tiptap.store";
import { useBuilderToolsStore } from "../store/builder-tools.store";
import { useMousePosition } from "../store/mouse-position.store";
import { useNotifications } from "../store/notifications.store";

import PageAction from '../components/page-action'
import Sidebar from '../components/sidebar'
import PdfHeader from '../components/pdf-builder/pdf-header'
import DocumentShareHeader from "../components/pdf-builder/document-share-header.vue";
import PdfBulletGenerator from '../components/pdf-builder/pdf-bullet-generator';
import ActionsBar from "../components/pdf-builder/actions-bar.vue";
import PdfDraggableTick from '../components/pdf-builder/pdf-draggable-tick';
import PdfRuler from '../components/pdf-builder/pdf-ruler';
import ScorerCard from '../components/resume-score/scorer-card.vue';
import SidebarScorer from '../components/resume-score/sidebar-scorer.vue';
import PDFContextMenu from "../components/pdf/pdf-context-menu.vue";
import ShareResumeModal from '../modals/resumes/share-resume-modal.vue';
import RemoveUserAccessModal from "../modals/remove-user-access.vue"

import SurveyModal from '../modals/survey';
import SelectResume from '../modals/select-resume';
import TailorResume from '../modals/tailor-resume';
import ChangeTemplateModal from "../modals/change-template";
import LimitExceededModal from '../modals/limit-exceeded.vue';
import BuilderOnboarding from "../modals/builder-onboarding";
import CoverLetterOnboarding from "../modals/cover-letter-onboarding";
import ResumeScoreModal from "../modals/resumes/resume-score.vue";
import ResumeServiceNudge from "../modals/services-nudges/service-nudge-modal.vue";

import BigSpinner from "../icons/big-spinner.vue";

const route = useRoute();
const router = useRouter();
const id = route.params.id;

const isOnCoverLetterPage = computed(() => {
    return route.meta?.coverLetter || false;
})

const { meta: { coverLetter = false }, params, query } = route;
const templates = ref([]);
const generatedPromptForCoverLetter = shallowRef(true);
const changeTemplateVisible = shallowRef(false);
const changeTemplateCalledFromSidebar = shallowRef(false);
const visibleSurvey = shallowRef(false);
const tailorResumeVisible = shallowRef(false);
const loading = shallowRef(false);
const showContextMenu = shallowRef(false);

const builder = useBuilder();
const tiptapStore = useTiptapStore();
const builderStyle = useBuilderStyles();
const bulletGenerator = useBulletGenerator();
const featuresUsageStore = useFeatureUsageStore();
const scorerStore = useResumeScorerStore();
const onboardingStore = useOnboardingStore();
const builderToolsStore = useBuilderToolsStore();
const mousePositionStore = useMousePosition();

const { token = null } = query;

const { selectedText, editor } = storeToRefs(tiptapStore);
const { opened: bulletOpened } = storeToRefs(bulletGenerator);
const { loaded, data, isDocumentShared } = storeToRefs(builder);
const { isOnboarding, openedCoverLetter } = storeToRefs(onboardingStore);
const { showResumeScorer } = storeToRefs(scorerStore);
const { page: pageStyles, ruler: rulerStyles, getRullerPaddings: rulerPaddings } = storeToRefs(builderStyle);
const { nrOfPages, currentPage, pageHeight } = storeToRefs(builderToolsStore);

const notificationsStore = useNotifications();

const setShowContextMenu = () => {
  showContextMenu.value = true;
}

const hideContextMenu = () => {
  showContextMenu.value = false;
}

const rightClickHandler = (event) => {
  if(isDocumentShared.value) return;

  event.preventDefault();
  mousePositionStore.mouseEventHandler(event);
  setShowContextMenu();
}

const changeTemplateHandler = () => {
  changeTemplateVisible.value = true;
  changeTemplateCalledFromSidebar.value = true
}

const regenerateCoverLetterPromptHandler = () => {
  generatedPromptForCoverLetter.value = false;
}

const handleNewResource = () => {
  if(isDocumentShared.value) return;

   // allow to change templates only for resumes
  if(! coverLetter) {
    if(! templates.value.length) {
      builder.init(null, router.replace, route)
      return
    } else {
      changeTemplateVisible.value = true
      changeTemplateCalledFromSidebar.value = false
    }
    
    return
  }

  builder.init(null, router.replace, route)
}

const handleChangeTemplate = ({template}) => {
  if(! changeTemplateCalledFromSidebar.value) {
    builder.init(null, router.replace, route)
    return
  }

  builder.changeTemplate(template)
}

const handleLimitError = () => {
  featuresUsageStore.setShowLimitExceed('cover_letter')
}

const handleCloseGeneratorPrompt = message => {
  generatedPromptForCoverLetter.value = true
  if(! message) return
  const formatteHTML = '<p>' + message.replace(/\n([ \t]*\n)+/g, '</p><p></p><p>').replace('\n', '<br />') + '</p>' 
  builder.changeMessage(formatteHTML);
}

const handleCloseTailorResume = () => {
  tailorResumeVisible.value = false
}

const showSurveyOnFirstDownload = () => {
  if(isDocumentShared.value) return;

  if (!localStorage.getItem("showSurveyOnFirstDownload")) {
    visibleSurvey.value = true
    localStorage.setItem("showSurveyOnFirstDownload", true)
  }
}

const handleCloseSurvey = () => {
  visibleSurvey.value = false
}

const handleRegenerateCoverLetter = (allText = false) => {
  if(isDocumentShared.value) return;

  loading.value = true
  let textToRegenerate

  if (allText) {
    textToRegenerate = editor.value.getText()
  }else{
    textToRegenerate = selectedText.value
  }

  regenerateCoverLetter(route.params.id, {
    // form data
    selected_text: textToRegenerate,
    previous_cover_letter: editor.value.getText(),
  })
    .catch((err) => {
      if (err?.response?.status === 429) {
          handleLimitError()
      }
    })
    .then(res => {
      let completion = res?.data?.completion

      // if completion is empty we not charge, and show error message.
      if(! completion?.replace(/\s/g, "").length ) {
        notificationsStore.notify('Internal error! Please try one more time.', 'error')
      } else {
        if(allText) {
            builder.updateMessage(editor.value, completion)
        } else {
            builder.updateSection(editor.value, completion )
        }
      }
    })
    .finally(() => {
      setTimeout(() => {
          loading.value = false
      }, 500)
    })
}

// keys binding
onMounted(() => {
  if(isDocumentShared.value) return;

  document.onkeydown = function(e) {
    if (["+", "="].includes(e.key) && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builderStyle.zoomIn()
    }

    if (e.key === "-" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builderStyle.zoomOut()
    }

    if (e.key === "0" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builderStyle.resetZoom()
    }

    if (e.key === "z" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builder.undo()
    }

    if (e.key === "x" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builder.redo()
    }

    if (e.key === "j" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builderStyle.textMinus()
    }

    if (e.key === "k" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        builderStyle.textPlus()
    }
  }
})

onMounted(() => {
  if(isDocumentShared.value) return;

  builderToolsStore.scrollEventListener();
});

onUnmounted(() => {
  if(isDocumentShared.value) return;

  document.removeEventListener("wheel", builderToolsStore.scrollEventListener);
})

onMounted(async () => {
    if(token == null) {
        builder.init(id, router.replace, route)
    }else{
        try {
            const sharedResume = await shareResumeApi.getResumeWithToken(token);

            builder.initUsingObject(sharedResume, {
                isDocumentEditable: false,
                showPersonalInformation: true,
                isDocumentShared: true
            })
        }catch (e) {
            router.push({ name: 'resume-not-found' })
        }
    }
})

onMounted(async () => {
    if(token == null) {
        templates.value = await getAllTemplates()
    }
})

watch(loaded, newval => {
  if(! newval || isDocumentShared.value) return;
  generatedPromptForCoverLetter.value = data.value.generated
})
</script>

<style>
.builder-content {
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
}

.builder-content svg {
  outline: none !important;
}
</style>
